import { useMemo, useState } from "react";
import { useMonthState } from "../../hooks/useMonthState.js";
import { calendarMonthDate } from "../../lib/monthState.js";
import { Button, Card, Col, DatePicker, Drawer, Form, Input, List, Popconfirm, Radio, Row, Space, TimePicker, Tooltip, Typography, theme } from "antd";
import { AppEmpty } from "../../components/Feedback.jsx";
import { MonthlyCalendarGrid } from "../../components/MonthlyCalendarGrid.jsx";
import { createStyles } from "antd-style";
import dayjs from "dayjs";
import koKR from "antd/locale/ko_KR";
import { CalendarDays, ChevronLeft, ChevronRight, Plus, Trash2 } from "lucide-react";
import { academyDateKey, formatDate } from "../../utils/date.js";
import { ACADEMY_CALENDAR_COLORS } from "../../utils/calendarColors.js";

const { RangePicker } = DatePicker;
const { Text } = Typography;

const calendarColors = ACADEMY_CALENDAR_COLORS;

const useStyle = createStyles(({ token, css }) => ({
  colorPicker: css`
    display: flex;
    gap: ${token.marginXXS}px;

    .ant-radio-wrapper { margin-inline-end: 0; }
    .ant-radio { position: absolute; opacity: 0; }
    .ant-radio-wrapper > span:last-child { padding-inline-start: 0; }
    .ant-radio-wrapper-checked .academy-event-color-swatch {
      outline: 2px solid ${token.colorPrimary};
      outline-offset: 2px;
    }
  `,
  colorSwatch: css`
    display: block;
    width: ${token.controlHeightSM}px;
    height: ${token.controlHeightSM}px;
    border: 1px solid ${token.colorBorder};
    border-radius: 50%;
  `
}));

function eventEndDate(event) {
  return event.endDate || event.date;
}

function isEventOnDate(event, date) {
  const current = dayjs(date);
  const start = dayjs(event.date);
  const end = dayjs(eventEndDate(event));
  return !current.isBefore(start, "day") && !current.isAfter(end, "day");
}

function getEventColor(type, token, color) {
  if (color) return color;
  const colors = {
    행사: token.colorSuccess,
    휴원: token.colorError,
    상담주간: token.colorWarning,
    기타: token.colorPrimary
  };
  return colors[type] || token.colorPrimary;
}

function getEvents(events, token) {
  return events.map((event) => ({
    key: event.id,
    label: `${event.time ? `${event.time} ` : ""}${event.title}`,
    title: event.title,
    start: dayjs(event.date),
    end: dayjs(eventEndDate(event)),
    color: getEventColor(event.type, token, event.color)
  }));
}

function buildInitialValues(selectedDate) {
  const date = dayjs(selectedDate);
  return {
    title: "",
    dates: [date, date],
    time: null,
    color: calendarColors[0].value,
    memo: ""
  };
}

export function AcademyCalendarPage({ store }) {
  const { state, update, createId } = store;
  const { token } = theme.useToken();
  const { styles } = useStyle();
  const [entryDate] = useState(academyDateKey);
  const [savedMonth, setSavedMonth] = useMonthState("month:calendar", () => entryDate.slice(0, 7));
  const [selectedDate, setSelectedDate] = useState(() => calendarMonthDate(savedMonth, entryDate));
  const [calendarValue, setCalendarCursor] = useState(() => dayjs(selectedDate));
  const setCalendarValue = value => { setCalendarCursor(value); setSavedMonth(value.format("YYYY-MM")); };
  const [modalOpen, setModalOpen] = useState(false);
  const [form] = Form.useForm();

  const selectedEvents = useMemo(() => state.events
    .filter((event) => isEventOnDate(event, selectedDate))
    .sort((a, b) => (a.time || "").localeCompare(b.time || "")), [selectedDate, state.events]);
  const events = useMemo(() => getEvents(state.events, token), [state.events, token]);

  function openCreate() {
    form.setFieldsValue(buildInitialValues(selectedDate));
    setModalOpen(true);
  }

  function closeModal() {
    setModalOpen(false);
    form.resetFields();
  }

  function moveMonth(amount) {
    const next = calendarValue.add(amount, "month");
    setCalendarValue(next);
    setSelectedDate(next.format("YYYY-MM-DD"));
  }

  function selectMonth(value) {
    if (!value) return;
    const next = value.startOf("month");
    setCalendarValue(next);
    setSelectedDate(next.format("YYYY-MM-DD"));
  }

  function goToday() {
    const today = dayjs(academyDateKey());
    setCalendarValue(today);
    setSelectedDate(today.format("YYYY-MM-DD"));
  }

  async function save(values) {
    const [start, end] = values.dates;
    const nextEvent = {
      id: createId("ev"),
      title: values.title.trim(),
      date: start.format("YYYY-MM-DD"),
      endDate: end.format("YYYY-MM-DD"),
      time: values.time ? values.time.format("HH:mm") : "",
      color: values.color,
      memo: values.memo?.trim() || ""
    };

    if (!await update((draft) => {
      draft.events.push(nextEvent);
    })) return;
    closeModal();
  }

  async function remove(id) {
    if (!await update((draft) => {
      draft.events = draft.events.filter((item) => item.id !== id);
    })) return;
  }

  return (
    <div className="calendar-page-grid academy-calendar-page">
      <Card className="academy-management-card calendar-main-card">
        <div className="academy-tab-panel">
          <div className="section-toolbar calendar-section-toolbar">
            <div className="academy-calendar-navigation">
              <div className="payment-month-selector payment-period-selector academy-calendar-period-selector" aria-label="달력 기준 년도와 월">
                <Button type="text" icon={<ChevronLeft size={18} />} onClick={() => moveMonth(-1)} aria-label="이전 달" title="이전 달" />
                <DatePicker
                  className="payment-month-picker"
                  picker="month"
                  locale={koKR}
                  value={calendarValue}
                  onChange={selectMonth}
                  format="YYYY년 M월"
                  suffixIcon={<CalendarDays size={17} />}
                  allowClear={false}
                  aria-label="기준 년도와 월 선택"
                />
                <Button type="text" icon={<ChevronRight size={18} />} onClick={() => moveMonth(1)} aria-label="다음 달" title="다음 달" />
              </div>
              <Button className="academy-calendar-today" onClick={goToday}>오늘</Button>
            </div>
            <Button className="academy-calendar-create" type="primary" icon={<Plus size={16} />} onClick={openCreate}>일정 등록</Button>
          </div>
          <MonthlyCalendarGrid
            month={calendarValue}
            ranges={events}
            selectedDate={selectedDate}
            ariaItemLabel="일정"
            onDateSelect={(dateKey, current, isOutside) => {
              setSelectedDate(dateKey);
              if (isOutside) setCalendarValue(current.startOf("month"));
            }}
          />
        </div>
      </Card>

      <Card className="academy-management-card calendar-agenda-card">
        <div className="academy-tab-panel">
          <div className="section-toolbar calendar-section-toolbar calendar-agenda-toolbar">
            <h3>{formatDate(selectedDate)} 일정 목록</h3>
            <span className="management-list-count">총 {selectedEvents.length}건</span>
          </div>
          <List
          className="calendar-agenda-list"
          dataSource={selectedEvents}
          locale={{ emptyText: <AppEmpty description="선택한 날짜에 등록된 일정이 없습니다." action={<Button type="primary" icon={<Plus size={15} />} onClick={openCreate}>일정 등록</Button>} /> }}
          renderItem={(event) => (
            <List.Item
              actions={[
                <Popconfirm
                  key="delete"
                  title="일정을 삭제할까요?"
                  okText="삭제"
                  cancelText="취소"
                  okButtonProps={{ danger: true }}
                  onConfirm={() => remove(event.id)}
                >
                  <Button danger type="text" size="small" icon={<Trash2 size={15} />} aria-label={`${event.title || "일정"} 삭제`} title="삭제" />
                </Popconfirm>
              ]}
            >
              <List.Item.Meta
                title={<Space size={8}><span className="calendar-agenda-color" style={{ backgroundColor: getEventColor(event.type, token, event.color) }} /><strong>{event.title}</strong></Space>}
                description={(
                  <Space direction="vertical" size={4}>
                    <Space size={6} wrap>
                      <Text type="secondary">
                        {formatDate(event.date)}
                        {eventEndDate(event) !== event.date ? ` - ${formatDate(eventEndDate(event))}` : ""}
                        {event.time ? ` ${event.time}` : ""}
                      </Text>
                    </Space>
                    {event.memo && <Text type="secondary">{event.memo}</Text>}
                  </Space>
                )}
              />
            </List.Item>
          )}
          />
        </div>
      </Card>

      <Drawer
        title="일정 등록"
        open={modalOpen}
        onClose={closeModal}
        placement="right"
        closable={{ placement: "end" }}
        width={440}
        className="calendar-event-drawer"
        footer={(
          <div className="overlay-footer-actions">
            <Button onClick={closeModal}>취소</Button>
            <Button type="primary" onClick={() => form.submit()}>일정 저장</Button>
          </div>
        )}
        destroyOnHidden
      >
        <Form
          className="calendar-event-form"
          form={form}
          layout="vertical"
          initialValues={buildInitialValues(selectedDate)}
          onFinish={save}
        >
          <Form.Item
            label="제목"
            name="title"
            rules={[{ required: true, whitespace: true, message: "제목을 입력하세요." }]}
          >
            <Input placeholder="일정 제목" />
          </Form.Item>
          <Row gutter={[12, 18]}>
            <Col xs={24} sm={16}>
              <Form.Item
                label="날짜"
                name="dates"
                rules={[{ required: true, message: "날짜를 선택하세요." }]}
              >
                <RangePicker className="wide-control" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={8}>
              <Form.Item label="시간" name="time">
                <TimePicker className="wide-control" format="HH:mm" minuteStep={5} placeholder="시간 선택" />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item label="캘린더 색상" name="color">
            <Radio.Group className={styles.colorPicker}>
              {calendarColors.map(({ value, label }) => (
                <Tooltip key={value} title={label}>
                  <Radio value={value} aria-label={`${label} 색상`}>
                    <span className={`${styles.colorSwatch} academy-event-color-swatch`} style={{ backgroundColor: value }} />
                  </Radio>
                </Tooltip>
              ))}
            </Radio.Group>
          </Form.Item>
          <Form.Item label="메모" name="memo">
            <Input.TextArea rows={4} placeholder="공유 전 확인할 내용을 적어두세요." />
          </Form.Item>
        </Form>
      </Drawer>
    </div>
  );
}
